import { useCallback, useMemo } from 'react';
import { AUTOSAVE_STATUS } from '@demandproai/shared';
import {
  buildEmptyPlan,
  normalizePlanForForm,
} from '../utils/lifecarePlanUtils';
import useLifecarePlanAutosave from './useLifecarePlanAutosave';
import useLifecareStudioForm from './useLifecareStudioForm';

const buildInitialFormValuesFromPlan = ({ plan, client }) => {
  if (plan) return normalizePlanForForm(plan);
  return buildEmptyPlan({
    clientName: client?.personalInfo?.clientName,
    currentAge: client?.personalInfo?.age,
    dateOfIncident: client?.caseInfo?.dateOfIncident,
  });
};

// Single-client lifecare plan editor: wires data -> autosave -> Formik for ONE client.
// Decoupled from page-level concerns (client switching, preview pipeline, navigation)
// so the lifecare studio's per-client form can stay focused on rendering and tab state.
const useLifecarePlanEditor = ({ caseId, client, plan, onManualEditSaved }) => {
  const clientId = client?.clientId || '';
  const initialFormValues = useMemo(() => buildInitialFormValuesFromPlan({ plan, client }), [plan, client]);
  const isNewPlan = useMemo(() => !plan, [plan]);

  const { triggerAutosave, autosaveStatus, flushAutosave } = useLifecarePlanAutosave({
    caseId,
    clientId,
    onSaveSuccess: onManualEditSaved,
  });

  const handleValuesChange = useCallback(
    (plan) => {
      if (!clientId) return;
      triggerAutosave({ plan });
    },
    [clientId, triggerAutosave]
  );

  const formState = useLifecareStudioForm({
    initialValues: initialFormValues,
    onValuesChange: handleValuesChange,
    autosaveKey: clientId,
  });

  return { 
    formState, 
    autosaveStatus, 
    isNewPlan, 
    triggerAutosave, 
    flushAutosave,
    isSavingManualEdit: autosaveStatus === AUTOSAVE_STATUS.SAVING
  };
};

export default useLifecarePlanEditor;
